import { useEffect, useState } from 'react';
// ─── Interface styles (look & feel) — Classic / Modern / … ──────────────────
// Each style is a set of CSS rules scoped under <html data-ui="<id>">.
// Classic = the original look (no attribute, no extra rules).
// The user's choice is saved on their user record (extra_data.uiStyle, see
// AppContext → setUserUiStyle) and cached on this browser so it applies before first paint.
//
// ADDING A NEW STYLE LATER
//   1. Add an entry to UI_STYLES below (id, label, hint, font).
//   2. Create styles/<id>-ui.css with every rule scoped under html[data-ui="<id>"]
//      (copy modern-ui.css as a starting point) and import it in index.js.
//   Nothing else changes — the picker in the user menu lists it automatically.
export const UI_STYLES = [
  { id: 'classic', label: 'Classic', hint: 'Original BarkahSuite look' },
  { id: 'modern',  label: 'Modern',  hint: 'Clean enterprise look — one primary colour, compact tables and summaries',
    font: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap' },
];
const KEY = 'barkah_ui_mode';
const FONT_ID = 'barkah-ui-font';

export const normalizeUiMode = (id) => (UI_STYLES.some(s => s.id === id) ? id : 'classic');

export function getUiMode() {
  try { return normalizeUiMode(localStorage.getItem(KEY)); } catch (_) { return 'classic'; }
}
export function rememberUiMode(id) {
  try { localStorage.setItem(KEY, normalizeUiMode(id)); } catch (_) {}
}

export function applyUiMode(id = getUiMode()) {
  const st = UI_STYLES.find(s => s.id === normalizeUiMode(id));
  const el = document.documentElement;
  if (!st || st.id === 'classic') el.removeAttribute('data-ui');
  else el.setAttribute('data-ui', st.id);
  if (st?.font && !document.querySelector(`link[data-ui-font="${st.id}"]`)) {   // web font only when that style is used
    const l = document.createElement('link');
    l.id = FONT_ID + '-' + st.id; l.rel = 'stylesheet'; l.href = st.font; l.setAttribute('data-ui-font', st.id);
    document.head.appendChild(l);
  }
  try { window.dispatchEvent(new Event('barkah-ui-mode')); } catch (_) {}
}

// Used where no user is logged in yet (e.g. login page): browser-only switch
export function setUiMode(id) { rememberUiMode(id); applyUiMode(id); }

export const isModernUi = () => !!document.documentElement.getAttribute('data-ui');   // any non-classic style

// React hook: current interface style id; re-renders when the user switches style
const _cur = () => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-ui')) || 'classic';
export function useUiMode() {
  const [m, setM] = useState(_cur);
  useEffect(() => {
    const f = () => setM(_cur());
    window.addEventListener('barkah-ui-mode', f);
    return () => window.removeEventListener('barkah-ui-mode', f);
  }, []);
  return m;
}

// ── Chart colours for non-classic styles ─────────────────────────────────────
// Bright module accents become the one primary colour on single-series charts;
// multi-series charts (donuts, bar lists) get a muted, evenly-weighted series palette.
const MODULE_ACCENTS = new Set(['#f97316','#ea580c','#e85d04','#8b5cf6','#7c3aed','#6366f1','#0ea5e9','#0284c7','#0d9488','#0f766e','#0891b2','#14b8a6','#3b82f6','#2563eb']);
const SERIES = {
  '#f97316':'#c98a3e', '#ea580c':'#c98a3e', '#e85d04':'#c98a3e', '#fb923c':'#d9a66a', '#f59e0b':'#c9a227', '#eab308':'#c9a227', '#d97706':'#b8862c',
  '#8b5cf6':'#7466b5', '#7c3aed':'#7466b5', '#a855f7':'#8c6bb1', '#6366f1':'#5d6fb8', '#ec4899':'#b5658f', '#db2777':'#a9587f',
  '#0ea5e9':'#3f86c4', '#3b82f6':'#1d5fb8', '#2563eb':'#1d5fb8', '#0284c7':'#2f75b5', '#06b6d4':'#3a8fb0', '#0891b2':'#3a8fb0',
  '#10b981':'#3f8f6a', '#16a34a':'#3f8f5f', '#22c55e':'#4f9d6b', '#059669':'#3a8463', '#0d9488':'#2f8f83', '#14b8a6':'#3a9a8e', '#0f766e':'#2f7f75',
  '#ef4444':'#c0504d', '#dc2626':'#b5443f', '#f43f5e':'#c25a6a',
};
export function chartTone(color, mode, single = false) {
  if (!color || !mode || mode === 'classic') return color;
  const c = String(color).toLowerCase();
  if (single && MODULE_ACCENTS.has(c)) return '#1d5fb8';
  return SERIES[c] || color;
}
